import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Row, Button, Col, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import validateRut from 'helpers/rutFormat';
import { get } from 'helpers/requests';
import { API, BASE_URL, PROFILES, ROLES } from 'CONST';
import { emailValidate } from 'helpers/helpers';
import { objectsToOptions, objectToOption } from '../helpers/selects';

const CreateUserForm = ({ userData, allProfiles, clientRoles, organizations, paises, onSubmit, edit, isOrsan, isOnlyCoordinator, currentUser }) => {
  const [rut, setRut] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isValidRut, setIsValidRut] = useState(false);
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cargo, setCargo] = useState('');
  const [isCoordinator, setIsCoordinator] = useState(false);
  const [canCreateLines, setCanCreateLines] = useState(false);
  const [telefono, setTelefono] = useState('');

  const [profile, setProfile] = useState(null);
  const [profilesOptions, setProfilesOptions] = useState([]);

  const [organization, setOrganization] = useState(null);
  const [organizationsOptions, setOrganizationsOptions] = useState([]);

  const [projects, setProjects] = useState([]);
  const [projectsOptions, setProjectsOptions] = useState([]);
  const [hasProjectsOptions, setHasProjectsOptions] = useState(false);

  const [organizationId, setOrganizationId] = useState('');
  const [username, setUsername] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [isDisabled, setIsDisabled] = useState(true);

  const [hasVentaEnVerde, setHasVentaEnVerde] = useState(false);

  const [tipoUsuario, setTipoUsuario] = useState('');

  const tipoUsuarioOptions = [
    { value: 'administrador', label: 'Administrador' },
    { value: 'operador', label: 'Operador' },
    { value: 'visualizador', label: 'Visualizador' },
  ];

  const tipoUsuarioOptionsMap = new Map(
    tipoUsuarioOptions.map(option => [option.value, option])
  );
  const [policySettings, setPolicySettings] = useState({});
  const [shouldReceiveAnexoMail, setShouldReceiveAnexoMail] = useState(true);
  const [shouldReceiveResumenDiarioMail, setShouldReceiveResumenDiarioMail] = useState(true);
  const [shouldReceiveDeclaracionLibroVenta, setShouldReceiveDeclaracionLibroVenta] = useState(false);
  const [shouldReceiveLiquidacion, setShouldReceiveLiquidacion] = useState(true);
  const [shouldReceiveDocuTributario, setShouldReceiveDocuTributario] = useState(true);
  const shouldDisableTipoUsuario = isOnlyCoordinator && !currentUser.is_orsan;

  const [policiesOptions, setPoliciesOptions] = useState([]);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const hasFetchedPolicies = useRef(false);

  const [pais, setPais] = useState(null);
  const [paisesOptions, setPaisesOptions] = useState([]);

  const isChileno = !pais || !pais.value || pais.value.code === 'CL';

  const [isValidatingDocumento, setIsValidatingDocumento] = useState(false);
  const [documentoValidationMessage, setDocumentoValidationMessage] = useState('');
  const [documentoHasValidators, setDocumentoHasValidators] = useState(true);
  const documentoDebounceRef = useRef(null);

  const validateForeignDocumento = (documento, paisCode) => {
    if (documentoDebounceRef.current) {
      clearTimeout(documentoDebounceRef.current);
    }

    if (!documento || !paisCode) {
      setIsValidRut(false);
      setDocumentoValidationMessage('');
      return;
    }

    setIsValidRut(false);
    setDocumentoValidationMessage('');

    documentoDebounceRef.current = setTimeout(async () => {
      setIsValidatingDocumento(true);
      try {
        const response = await get(
          `${BASE_URL}${API.paisesValidarDocumento}?code=${paisCode}&documento=${encodeURIComponent(documento)}`
        );
        setIsValidRut(response.is_valid);
        setDocumentoHasValidators(response.has_validators);
        if (!response.has_validators) {
          setDocumentoValidationMessage('No hay validadores disponibles para este país; se acepta cualquier formato.');
        } else if (response.is_valid) {
          setDocumentoValidationMessage('Documento válido.');
        } else {
          setDocumentoValidationMessage(`Documento inválido. Se intentó: ${response.tested_types.join(', ')}.`);
        }
      } catch (error) {
        setIsValidRut(false);
        setDocumentoValidationMessage('No se pudo validar el documento. Intente nuevamente.');
      } finally {
        setIsValidatingDocumento(false);
      }
    }, 400);
  };


  useEffect(() => {
    let isMounted = true;
    get(`${BASE_URL}${API.paises}`)
      .then((response) => {
        if (!isMounted) return;
        const paisesList = response.results || response;
        const options = objectsToOptions(paisesList, 'name');
        setPaisesOptions(options);
        if (!edit && !pais) {
          const chile = options.find(opt => opt.value.code === 'CL');
          if (chile) setPais(chile);
        }
      })
      .catch(() => {
        if (isMounted) setPaisesOptions([]);
      });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!userData || Object.keys(userData).length === 0 || !userData.rut) return;
    if (paisesOptions.length === 0) return;

    const foreignMatch = parseRutForCountry(userData.rut, paisesOptions);
    if (foreignMatch) {
      setPais(foreignMatch.paisOption);
      setDocumentoValidation(foreignMatch.documentNumber);
      setIsValidRut(true);
    } else {
      setRutValidation(userData.rut);
      const chileOption = paisesOptions.find(opt => opt.value.code === 'CL');
      if (chileOption) setPais(chileOption);
    }
  }, [userData, paisesOptions]);

  const setDocumentoValidation = (data) => {
    const cleaned = data.replace(/\s/g, '');
    setRut(cleaned);
  };

  const onChangeRut = (event) => {
    const value = event.target.value;
    if (isChileno) {
      setRutValidation(value);
    } else {
      setDocumentoValidation(value);
      validateForeignDocumento(value, pais && pais.value && pais.value.code);
    }
  };

  const onChangePais = (option) => {
    const willBeChileno = !option || !option.value || option.value.code === 'CL';
    setPais(option);
    if (willBeChileno !== isChileno) {
      setRut('');
      setIsValidRut(false);
      setDocumentoValidationMessage('');
    }
  };

  const parseRutForCountry = (rutValue, options) => {
    if (!rutValue || !options || options.length === 0) return null;
    const match = rutValue.match(/^([A-Z]{2})(.+)$/);
    if (!match) return null;
    const [, code, documentNumber] = match;
    if (code === 'CL') return null;
    const foundOption = options.find(opt => opt.value.code === code);
    return foundOption ? { paisOption: foundOption, documentNumber } : null;
  };

  const getOption = (options, id) => {
    return objectsToOptions(
      options.filter(option => option.value === id || option.value.id === id),
      'label',
    )[0]
  }

  const setRutValidation = (data) => {
    const rutData = data.replace(/[A-J]*[L-Z]*[a-j]*[l-z]*/g, '');
    const formatRut = validateRut(rutData);
    setRut(rutData.length > 0 ? formatRut.formatRut : rutData);
    setIsValidRut(formatRut.isValid)
  }

  const setEmailValidation = (data) => {
    setEmail(data);
    setIsValidEmail(emailValidate(data));
  }

  const setDataEdition = (data) => {
    const isForeignRutFormat = /^[A-Z]{2}/.test(data.rut);
    if (!isForeignRutFormat) {
      setRutValidation(data.rut);
    }    setEmail(data.email);
    setIsValidEmail(emailValidate(data.email));
    setFirstName(data.first_name);
    setLastName(data.last_name);
    setCargo(data.job_title);
    setIsCoordinator(data.is_coordinator);
    setTelefono(data.phone);
    setOrganizationId(data.organization_id);
    setUsername(data.username);
    setIsActive(data.is_active);

    const isCorredor = data.organization_roles && 
      data.organization_roles.includes('intermediariocredito');

    if (isCorredor) {
      setShouldReceiveAnexoMail(true);
      setShouldReceiveResumenDiarioMail(true);
      setShouldReceiveLiquidacion(data.should_receive_liquidacion != null ? data.should_receive_liquidacion : true);
    } else {
      setShouldReceiveAnexoMail(data.should_receive_anexo_mail);
      setShouldReceiveResumenDiarioMail(data.should_receive_resumendiario_mail);
      setShouldReceiveDeclaracionLibroVenta(data.should_receive_declaracion_libro_venta);
      setShouldReceiveLiquidacion(data.should_receive_liquidacion);
      setShouldReceiveDocuTributario(data.should_receive_docu_tributario_mail);
    }

    if (data.profiles.length !== 0) {
      setProfile(objectToOption(data.profiles[0], 'nombre'));
    }

    if (organizations && data.organization_id) {
      const selectedOrg = getOption(organizations, data.organization_id);
      setOrganization(selectedOrg);
    }
    if (organizations && data.projects_organization_id) {
      setOrganization(getOption(organizations, data.projects_organization_id));
    }
    if (hasProjectsOptions && data.projects_ids.length !== 0) {
      setProjects(data.projects_ids.map(project => getOption(projectsOptions, project).value));
    }

    if (data.should_receive_mail_corredor_policy_nemotecnicos && userData.organization_roles.includes('intermediariocredito')) {
      const policySettingsMap = {};
      data.should_receive_mail_corredor_policy_nemotecnicos.forEach(policy => {
        policySettingsMap[policy.nemotecnico] = {
          excludeGeneral: true,
          excludeAnexo: policy.excludeAnexo || false,
          excludeResumenDiario: policy.excludeResumenDiario || false
        };
      });
      setPolicySettings(policySettingsMap);
    } else {
      setPolicySettings({});
    }
  }

  const handlePolicyAnexoSelection = (option) => {
    setPolicySettings(prev => ({
      ...prev,
      [option.value]: {
        excludeGeneral:
          prev[option.value] && prev[option.value].excludeGeneral
            ? prev[option.value].excludeGeneral
            : false,

        excludeAnexo: !(
          prev[option.value] &&
          prev[option.value].excludeAnexo
            ? prev[option.value].excludeAnexo
            : false
        )
      }
    }));
  };


  const handlePolicyResumenDiarioSelection = (option) => {
    setPolicySettings(prev => ({
      ...prev,
      [option.value]: {
        excludeGeneral: prev[option.value] && prev[option.value].excludeGeneral ? prev[option.value].excludeGeneral : false,
        excludeAnexo: prev[option.value] && prev[option.value].excludeAnexo ? prev[option.value].excludeAnexo : false,
        excludeResumenDiario: !(prev[option.value] && prev[option.value].excludeResumenDiario ? prev[option.value].excludeResumenDiario : false)
      }
    }));
  };

  const fetchPolicies = async (intermediarioId, preserveSelections = false) => {
    if (!intermediarioId) {
      setPoliciesOptions([]);
      return;
    }
    if (hasFetchedPolicies.current === intermediarioId) {
      return;
    }
    try {
      const response = await get(`${BASE_URL}${API.polizasCredito}?intermediario_id=${intermediarioId}`);
      const policiesOptions = response.map(policy => ({
            value: policy.nemotecnico,
            label: policy.nemotecnico,
            id: policy.id,
            nemotecnico: policy.nemotecnico,
            asegurado_name: policy.asegurado.name
          }));
      hasFetchedPolicies.current = intermediarioId;
      setPoliciesOptions(policiesOptions);
      if (!preserveSelections) {
        setSelectedPolicies([]);
      }
    } catch (error) {
      setPoliciesOptions([]);
      if (!preserveSelections) {
        setSelectedPolicies([]);
      }
    }
  }

  const setOrganizationData = (data) => {
    setOrganization(data);
    setProjects([]);
    setPolicySettings({});
    setSelectedPolicies([]);
    if (data && data.value) {
      fetchPolicies(data.value.value, false);
    } else {
      setPoliciesOptions([]);
    }
  }

  const getProjects = () => {
    if (profile && profile.value && profile.value.slug === PROFILES.externoProyecto) {
      return projects ? projects.map(project => project.value.id) : [];
    }
    if (profile && profile.value && profile.value.slug === PROFILES.externoOrganizacion) {
      return []
    }
    return []
  }

  const handleSubmit = () => {
    const excludedPolicies = policiesOptions
      .filter(option => {
        const setting = policySettings[option.value];
        return setting && (setting.excludeAnexo || setting.excludeResumenDiario);
      })
      .map(option => {
        const setting = policySettings[option.value];
        return {
          nemotecnico: option.nemotecnico,
          id: option.id,
          excludeAnexo: setting.excludeAnexo,
          excludeResumenDiario: setting.excludeResumenDiario
        };
      });
    const finalRut = isChileno ? rut : `${pais.value.code}${rut}`;
    const data = {
      rut: finalRut,
      email,
      phone: telefono,
      is_coordinator: isCoordinator,
      can_create_lines: canCreateLines,
      job_title: cargo,
      organization_id: organizationId,
      first_name: firstName,
      last_name: lastName,
      is_active: isActive,
      should_receive_anexo_mail: shouldReceiveAnexoMail,
      should_receive_resumendiario_mail: shouldReceiveResumenDiarioMail,
      should_receive_declaracion_libro_venta: shouldReceiveDeclaracionLibroVenta,
      should_receive_liquidacion: shouldReceiveLiquidacion,
      should_receive_docu_tributario_mail: shouldReceiveDocuTributario,
      profiles_set: profile && profile.value ? [profile.value.id] : [],
      projects_organization_id: organization && organization.value && organization.value.value,
      projects_ids: getProjects(),
      should_receive_mail_corredor_policy_nemotecnicos: excludedPolicies,
    }

    if (edit){
      data.username = username
    }

    onSubmit(data);
  }

  useEffect(() => {
    setIsDisabled(!(isValidRut && firstName && lastName && cargo && tipoUsuario && pais));
  }, [isValidRut, firstName, lastName, cargo, tipoUsuario, pais]);

  useEffect(() => {
    if (organization && organization.value) {
      get(`${BASE_URL}${API.projects}?organizacion_id=${organization.value.value}`).then((response) => {
        setProjectsOptions(objectsToOptions(response, 'nombre'));
        setHasProjectsOptions(true);
      });
    }
  }, [organization]);

  useEffect(() => {    
    if (userData && Object.keys(userData).length !== 0) {
      setDataEdition(userData);
      let currentTipoUsuario = tipoUsuarioOptionsMap.get('visualizador');

      const hasExternoProfile = userData.profiles && userData.profiles.some(
        profile => profile.slug === PROFILES.externoAdministradorAseguradoCredito
      );

      const hasIntermediarioCreditoRole = userData.organization_roles && userData.organization_roles.includes('intermediariocredito');

      if (hasExternoProfile || hasIntermediarioCreditoRole) {
        setCanCreateLines(true);
        currentTipoUsuario = tipoUsuarioOptionsMap.get(userData.is_coordinator ? 'administrador' : 'operador');
      }

      if (!tipoUsuario) {
        setTipoUsuario(currentTipoUsuario);
      }
      setIsLoading(false);
      if (userData.organization_id) {
        fetchPolicies(userData.organization_id, true);
      }
    } else if (!edit) {
      setIsLoading(false);
    }
    if (allProfiles) {
      setProfilesOptions(objectsToOptions(allProfiles, 'nombre'));
    }
    if (organizations) {
      setOrganizationsOptions(objectsToOptions(organizations, 'label'));
    }
  }, [userData, allProfiles, organizations, hasProjectsOptions, edit]);

  useEffect(() => {
    if (!clientRoles) return;

    const ventaEnVerdeRoles = [ROLES.afianzadoVentaVerde, ROLES.beneficiarioVentaVerde, ROLES.aseguradoVentaVerde];

    setHasVentaEnVerde(ventaEnVerdeRoles.some(elem => clientRoles.includes(elem)));
  }, [clientRoles]);


  const onChangeProfile = (event) => {
    setProfile(event);
    setOrganization(null);
    setProjects([]);
  }

  const onChangeTipoUsuario = (event) => {
    setTipoUsuario(event);

    setIsCoordinator(event.value === 'administrador');
    setCanCreateLines(['administrador', 'operador'].includes(event.value));
  }

  return (
    <div>
      <div className="form-box form-group">
        <Col md="8">
          <p className="form-title">Información empleado</p>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="pais">
              País
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Select
                id="pais"
                name="pais"
                clearable={false}
                removeSelected={false}
                placeholder="Seleccione un país"
                options={paisesOptions}
                onChange={onChangePais}
                value={pais}
              />
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="rut">
              {isChileno ? 'RUT' : 'N° Documento'}
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Input
                name="rut"
                id="rut"
                onChange={onChangeRut}
                value={rut}
                invalid={isChileno ? (!!rut && rut.length > 3 && !isValidRut) : (!!rut && !isValidatingDocumento && !isValidRut)}
              />
              <FormFeedback style={{ fontSize: '12px', margin: '0 0 5px 5px' }}>
                {isChileno
                  ? (rut && rut.length > 3 && !isValidRut ? 'Rut inválido.' : '')
                  : ''}
              </FormFeedback>
              {!isChileno && (
                <div style={{ fontSize: '12px', margin: '2px 0 5px 5px', color: isValidatingDocumento ? '#6c757d' : (isValidRut ? '#28a745' : '#dc3545') }}>
                  {isValidatingDocumento ? 'Validando documento…' : documentoValidationMessage}
                </div>
              )}
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="email">
              Dirección de correo
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Input
                readOnly={edit}
                name="email"
                id="email"
                onChange={(event) => setEmailValidation(event.target.value)}
                value={email}
                invalid={!!email && email.length > 2 && !isValidEmail}
              />
              <FormFeedback style={{ fontSize: '12px', margin: '0 0 5px 5px' }}>
                {!isValidEmail ? 'Email inválido.' : ''}
              </FormFeedback>
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="name">
              Nombre
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Input
                type="name"
                name="firstName"
                id="first_name"
                onChange={(event) => setFirstName(event.target.value)}
                value={firstName}
              />
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="lastNames">
              Apellidos
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Input
                type="lastNames"
                name="lastName"
                id="last_name"
                onChange={(event) => setLastName(event.target.value)}
                value={lastName}
              />
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="position">
              Cargo
              <span className="required text-danger">*</span>
            </Label>
            <Col md={9}>
              <Input
                type="position"
                name="jobTitle"
                id="job_title"
                onChange={(event) => setCargo(event.target.value)}
                value={cargo}
              />
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="tipo-usuario">
              Tipo de usuario
            </Label>
            <Col md={9}>
              <Select
                id="tipo-usuario"
                name="tipo-usuario"
                removeSelected={false}
                placeholder="Seleccione un tipo de usuario"
                options={tipoUsuarioOptions}
                onChange={(event) => onChangeTipoUsuario(event)}
                value={tipoUsuario}
                isDisabled={ isLoading || shouldDisableTipoUsuario}
              />
              {shouldDisableTipoUsuario && (
                <div style={{ fontSize: '12px', color: '#ff6b35', marginTop: '5px' }}>
                  No puede cambiar el tipo de usuario porque es el único coordinador
                </div>
              )}
            </Col>
          </FormGroup>
          <FormGroup className="row">
            <Label className="col-sm-2 col-form-label" for="phone">
              Teléfono
            </Label>
            <Col md={9}>
              <Input
                type="phone"
                name="phone"
                id="phone"
                value={telefono}
                onChange={(event) => setTelefono(event.target.value)}
                placeholder="Ingrese teléfono"
              />
            </Col>
          </FormGroup>
          {clientRoles && clientRoles.includes(ROLES.aseguradoCredito) && (
            <>
              <FormGroup className="row">
                <Label className="col-sm-3 col-form-label" for="receiveResumenDiario">
                  Recibir correos Resumen diario
                </Label>
                <Col md={9}>
                  <Input
                    type="checkbox"
                    name="receiveResumenDiario"
                    id="receiveResumenDiario"
                    checked={shouldReceiveResumenDiarioMail}
                    onChange={(event) => setShouldReceiveResumenDiarioMail(event.target.checked)}
                  />
                </Col>
              </FormGroup>
              <FormGroup className="row">
                <Label className="col-sm-3 col-form-label" for="receiveAnexoMail">
                  Recibir correos Anexos de Clasificación
                </Label>
                <Col md={9}>
                  <Input
                    type="checkbox"
                    name="receiveAnexoMail"
                    id="receiveAnexoMail"
                    checked={shouldReceiveAnexoMail}
                    onChange={(event) => setShouldReceiveAnexoMail(event.target.checked)}
                  />
                </Col>
              </FormGroup>
              <FormGroup className="row">
                <Label className="col-sm-3 col-form-label" for="receiveDeclaracionLibroVentaMail">
                  Recibir correos Declaración Libro Venta
                </Label>
                <Col md={9}>
                  <Input
                    type="checkbox"
                    name="receiveDeclaracionLibroVentaMail"
                    id="receiveDeclaracionLibroVentaMail"
                    checked={shouldReceiveDeclaracionLibroVenta}
                    onChange={(event) => setShouldReceiveDeclaracionLibroVenta(event.target.checked)}
                  />
                </Col>
              </FormGroup>
              <FormGroup className="row">
                <Label className="col-sm-3 col-form-label" for="receiveDocumentoTributario">
                  Recibir correos Documentos Tributarios
                </Label>
                <Col md={9}>
                  <Input
                    type="checkbox"
                    name="receiveDocumentoTributario"
                    id="receiveDocumentoTributario"
                    checked={shouldReceiveDocuTributario}
                    onChange={(event) => setShouldReceiveDocuTributario(event.target.checked)}
                  />
                </Col>
              </FormGroup>
            </>
          )}
      {policiesOptions.length > 0 && userData &&
        userData.organization_roles.includes('intermediariocredito') && (
        <>
          <FormGroup className="row">
            <Label className="col-sm-3 col-form-label" for="receiveLiquidacion">
              Recibir correos Liquidación
            </Label>
            <Col md={9}>
              <Input
                type="checkbox"
                name="receiveLiquidacion"
                id="receiveLiquidacion"
                checked={shouldReceiveLiquidacion}
                onChange={(event) => setShouldReceiveLiquidacion(event.target.checked)}
              />
            </Col>
          </FormGroup>
      
          <FormGroup>
            <div className="form-field">
              <label>Pólizas del Intermediario</label>
        
              <>
                <div className="help-text" style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                  <strong>Por defecto recibirá correos para TODAS las pólizas.</strong><br />
                  Deseleccione las pólizas para las que <strong>NO</strong> desea recibir correos.
                </div>
        
                <div style={{ border: '1px solid #ddd', borderRadius: '6px', padding: '10px' }}>
                  <div className="row" style={{ fontWeight: 'bold', marginBottom: '10px', padding: '0 10px' }}>
                    <div className="col-5">Póliza</div>
                    <div className="col-4">Anexo clasificación</div>
                    <div className="col-3">Resumen diario</div>
                  </div>
        
                  {policiesOptions.map(option => {
                    const policySetting =
                      policySettings[option.value] || {
                        excludeGeneral: false,
                        excludeAnexo: false,
                        excludeResumenDiario: false
                      };
                    
                    return (
                      <div
                        key={option.value}
                        className="row align-items-center mb-2"
                        style={{ padding: '5px 10px', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <div className="col-5">
                          <label style={{ margin: 0, fontWeight: '500' }}>
                            {option.label}
                            {option.asegurado_name && (
                              <div style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'normal' }}>
                                {option.asegurado_name}
                              </div>
                            )}
                          </label>
                        </div>
                    
                        <div className="col-4">
                          <input
                            type="checkbox"
                            checked={!policySetting.excludeAnexo}
                            onChange={() => handlePolicyAnexoSelection(option)}
                            style={{ marginRight: '8px' }}
                          />
                          <small style={{ color: '#6c757d' }}>
                            {policySetting.excludeAnexo ? 'Excluida' : 'Incluida'}
                          </small>
                        </div>
                    
                        <div className="col-3">
                          <input
                            type="checkbox"
                            checked={!policySetting.excludeResumenDiario}
                            onChange={() => handlePolicyResumenDiarioSelection(option)}
                            style={{ marginRight: '8px' }}
                          />
                          <small style={{ color: '#6c757d' }}>
                            {policySetting.excludeResumenDiario ? 'Excluida' : 'Incluida'}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="help-text">
                  {policiesOptions.length} póliza(s) disponible(s).
                  {Object.values(policySettings).filter(
                    setting =>
                      setting.excludeGeneral ||
                      setting.excludeAnexo ||
                      setting.excludeResumenDiario
                  ).length > 0 ? (
                    <span style={{ color: '#ff6b35' }}>
                      {' '}
                      ({Object.values(policySettings).filter(
                        setting =>
                          setting.excludeGeneral ||
                          setting.excludeAnexo ||
                          setting.excludeResumenDiario
                      ).length}{' '}
                      con exclusiones configuradas)
                    </span>
                  ) : (
                    <span style={{ color: '#28a745' }}>
                      {' '}
                      (Todas activas para notificaciones)
                    </span>
                  )}
                </div>
              </>
            </div>
          </FormGroup>
        </>
        )}
        </Col>
      </div>
      {!isOrsan && (hasVentaEnVerde || profile) && (
        <div className="form-box">
          <Col md="8">
            {hasVentaEnVerde && (
              <>
                <p className="form-title">Asignación a proyectos (Opcional)</p>
                <FormGroup className="row">
                  <Label className="col-sm-2 col-form-label" for="perfiles">
                    Perfil
                  </Label>
                  <Col md={10}>
                    <Select
                      id="perfil"
                      name="perfil"
                      isClearable
                      removeSelected={false}
                      placeholder="Seleccione un perfil"
                      options={profilesOptions}
                      onChange={(event) => onChangeProfile(event)}
                      value={profile}
                    />
                  </Col>
                </FormGroup>
              </>
            )}
            { profile &&
              (
                <>
                  <FormGroup className="row">
                    <Label className="col-sm-2 col-form-label" for="organizacion">
                      Organización
                    </Label>
                    <Col md={10}>
                      <Select
                        id="organizacion"
                        name="organizacion"
                        clearable={false}
                        removeSelected={false}
                        placeholder="Seleccione una organización"
                        options={organizationsOptions}
                        onChange={(event) => setOrganizationData(event)}
                        value={organization}
                      />
                    </Col>
                  </FormGroup>
                  { profile && profile.value && profile.value.slug === PROFILES.externoProyecto &&
                    (
                      <FormGroup className="row">
                        <Label className="col-sm-2 col-form-label" for="proyecto">
                          Proyecto
                        </Label>
                        <Col md={10}>
                          <Select
                            id="proyecto"
                            name="proyecto"
                            clearable={false}
                            removeSelected={false}
                            placeholder="Seleccione al menos un proyecto"
                            options={projectsOptions}
                            onChange={(event) => setProjects(event)}
                            value={projects}
                            isMulti
                            getOptionValue={project => project.value.id}
                          />
                        </Col>
                      </FormGroup>
                    )
                  }
                </>
              )
            }
          </Col>
        </div>
      )}
      <div className="form-box">
        <Col md="8">
          <Row>
            <div className="col-md-4">
              <Button
                style={{
                  width: '100%',
                  backgroundColor: '#0055B8',
                  border: '#0055B8',
                }}
                disabled={isDisabled}
                onClick={handleSubmit}
              >
                <FontAwesomeIcon icon={faSave} />
                Guardar
              </Button>
            </div>
          </Row>
        </Col>
      </div>
    </div>
  );
}

CreateUserForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  userData: PropTypes.shape({
    email: PropTypes.string,
    first_name: PropTypes.string,
    id: PropTypes.number,
    is_active: PropTypes.bool,
    job_title: PropTypes.string,
    last_name: PropTypes.string,
    organization_id: PropTypes.number,
    projects_organization_id: PropTypes.number,
    phone: PropTypes.string,
    rut: PropTypes.string,
    username: PropTypes.string,
    is_coordinator: PropTypes.bool,
    projects_ids: PropTypes.arrayOf(PropTypes.number),
    profiles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        slug: PropTypes.string,
        nombre: PropTypes.string,
      }),
    ),
  }),
  allProfiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      slug: PropTypes.string,
      nombre: PropTypes.string,
    }),
  ).isRequired,
  clientRoles: PropTypes.arrayOf(PropTypes.string),
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      nombre_fantasia: PropTypes.string,
    }),
  ),
  isOrsan: PropTypes.bool,
  isOnlyCoordinator: PropTypes.bool.isRequired
}

CreateUserForm.defaultProps = {
  userData: {},
  clientRoles: [],
  organizations: [],
  edit: false,
  isOrsan: true,
};
const mapStateToProps = (state) => ({
  currentUser: state.login.user
});

export default connect(mapStateToProps)(CreateUserForm);